<template>
  <div class="movie-meta-info-area" :class="{
      'full-mode': isViewModeFull,
      'compare-mode': isViewModeComp,
    }"
  >
    <div
      class="corner-triangle-label"
      v-if="isViewModeComp"
      :style="{opacity: cornerLabelOpacity}"
    ></div>
    <div class="row1 mb8">
      <div class="date ib mr10">
        <label class="form-control-label mr4">日付</label>
        <input type="text" class="form-control ib" :value="dateDisp" readonly>
      </div>
      <div class="time ib">
        <label class="form-control-label mr4">時刻</label>
        <input
          type="text"
          class="form-control ib mr4"
          maxlength="8"
          v-model="state.timeDisp"
          :readonly="!state.isEditingTime"
          @keyup.enter="moveToEditedTime"
        >
        <button
          class="btn btn-primary btn-sm"
          key="startTimeEdit"
          v-if="!state.isEditingTime"
          :disabled="!enableTimeSeek"
          @click="startTimeEdit"
        >
          <span>時刻指定</span>
        </button>
        <button
          class="btn btn-primary btn-sm"
          key="moveToEditedTime"
          v-if="state.isEditingTime"
          @click="moveToEditedTime"
          :disabled="!isValidTimeDispValue"
        >
          <span>移動</span>
        </button>
        <button
          class="btn btn-secondary btn-sm"
          key="cancelTimeEdit"
          v-if="state.isEditingTime"
          @click="cancelTimeEdit"
        >
          <span>キャンセル</span>
        </button>
      </div>
      <div class="spacer"></div>
      <div class="ta-right mt4 ml4" v-tooltip="copyLinkTooltip">
        <button
          class="btn btn-secondary btn-sm"
          :disabled="!canCopyLink"
          v-if="!state.isEditingTime"
          @click="copyLink"
        >
          <span><i class="far fa-copy"></i>リンクをコピー</span>
        </button>
      </div>
    </div>
    <div class="row2 mb8">
      <div class="location ib">
        <label class="form-control-label mr4">場所</label>
        <input type="text" class="form-control ib" :value="locationDisp" readonly>
      </div>
    </div>
    <div class="row3 mb8">
      <button class="btn btn-info btn-sm mr4 edit-tag-btn" @click="editMovieTags">
        <i class="fas fa-tag"></i>
        <span v-if="movieTags.length === 0">タグ編集</span>
      </button>
      <div class="tags">
        <span
          class="tag-badge mr4 mb4 "
          :class="'tag-badge-' + e.tagType1"
          v-for="(e, i) in movieTags"
          :key="i"
        >
          {{e.tagName}}
        </span>
      </div>
    </div>
    <div class="row4">
      <div class="three-axis-sensor ib">
        <template v-if="enableSensorChart">
          <button class="btn btn-secondary btn-sm" v-if="!state.showSensorChart" @click="openSensorChart">
            三軸センサー表示
          </button>
          <button class="btn btn-secondary btn-sm" v-else @click="closeSensorChart">
            三軸センサー非表示
          </button>
        </template>
      </div>
      <div class="speed-meter">
        <span class="speed text-primary mr4">{{velocityDisp}}</span>
        <span class="unit">km/h</span>
      </div>
    </div>
    <div class="row5 mt8" v-show="state.showSensorChart">
      <ThreeAxisSensorChart :sensor-data="state.sensorData" @click="onSensorChartClick" />
    </div>
  </div>
</template>

<script setup lang="ts">
import ThreeAxisSensorChart from 'src/components/lib/ThreeAxisSensorChart/index.vue';
import {
  computed,
  nextTick,
  reactive,
  watch,
} from 'vue';
import type { SensorData } from 'src/models/mtx';
import type { GIMovie, GIMovieGeoIndex } from 'src/models/geoItem';
import { dtFormat, ensureDate } from 'src/lib/dateHelper';

interface MovieMetaInfoAreaState {
  isEditingTime: boolean;
  showSensorChart: boolean;
  timeDisp: string;
  sensorData: SensorData[];
}

interface Props {
  movie: GIMovie | null;
  movieGeoIndex: GIMovieGeoIndex | null;
  movieCurrentDt: Date | null;
  enableSensorChart: boolean;
  enableTimeSeek: boolean;
  viewMode: 'full' | 'comp';
  cornerLabelOpacity?: number;
  locationDisp?: string;
}

const props = withDefaults(defineProps<Props>(), {
  cornerLabelOpacity: 1.0,
  locationDisp: '',
});

const emit = defineEmits<{
  (e: 'start-time-edit'): void;
  (e: 'cancel-time-edit'): void;
  (e: 'move-to-time', payload: { dt: Date; dateDisp: string; timeDisp: string }): void;
  (e: 'edit-movie-tags'): void;
  (e: 'try-show-new-sensor-chart'): void;
}>();

const state = reactive<MovieMetaInfoAreaState>({
  isEditingTime: false,
  showSensorChart: false,
  timeDisp: '',
  sensorData: [],
});
const dateDisp = computed<string>(() => {
  if (!props.movieGeoIndex) return '';

  return dtFormat(props.movieGeoIndex.ts, 'yyyy/mm/dd');
});
const velocityDisp = computed<string>(() => {
  if (!props.movieGeoIndex) return '---';

  const velo = parseInt(props.movieGeoIndex.velocity ?? '');
  return isNaN(velo) ? '---' : velo.toString();
});

watch(() => props.movieCurrentDt, () => {
  if (!props.movieCurrentDt) {
    state.timeDisp = '';
    return;
  }
  state.timeDisp = dtFormat(props.movieCurrentDt, 'HH:MM:SS');
});

// computed:
const isViewModeFull = computed(() => {
  return props.viewMode === 'full';
});
const isViewModeComp = computed(() => {
  return props.viewMode === 'comp';
});
const isValidTimeDispValue = computed(() => {
  const arr = state.timeDisp.split(':')
    .filter(e => e.match(/^\d\d$/)).map(e => parseInt(e));
  if (arr.length !== 3) return false;

  const hour = arr[0];
  const minute = arr[1];
  const second = arr[2];
  if (
    0 <= hour && hour < 24 &&
    0 <= minute && minute < 60 &&
    0 <= second && second < 60
  ) {
    return true;
  }
  return false;
});
const movieTags = computed(() => {
  if (!props.movie) return [];

  return props.movie.tags;
});
const canCopyLink = computed(() => {
  return !!window.navigator.clipboard;
});
const copyLinkTooltip = computed(() => {
  return canCopyLink.value ? '' : 'http環境ではこの機能は使えません';
});

// methods:
const setSensorData = (sensorData: SensorData[]) => {
  state.sensorData = sensorData;
};
const setAllUIStateToDefault = () => {
  state.isEditingTime = false;
  state.showSensorChart = false;
  setSensorData([]);
};

let timeDispOrig = '';
const startTimeEdit = () => {
  timeDispOrig = state.timeDisp;
  state.isEditingTime = true;
  emit('start-time-edit');
};
const cancelTimeEdit = () => {
  state.timeDisp = timeDispOrig;
  state.isEditingTime = false;
  emit('cancel-time-edit');
};
const moveToEditedTime = () => {
  if (!state.isEditingTime || !isValidTimeDispValue.value) return;

  const timeDisp = state.timeDisp;
  const dt = ensureDate(`${dateDisp.value} ${timeDisp}`);
  if (!dt) return;

  emit('move-to-time', { dt, dateDisp: dateDisp.value, timeDisp });
  if (document.activeElement) {
    // time-inputにフォーカスが残ったままになるのが嫌なので
    (document.activeElement as HTMLElement).blur();
  }
  nextTick(() => {
    state.isEditingTime = false;
  });
};
const editMovieTags = () => {
  emit('edit-movie-tags');
};
const openSensorChart = () => {
  state.showSensorChart = true;
  if (state.sensorData.length === 0) {
    emit('try-show-new-sensor-chart');
  }
};
const closeSensorChart = () => {
  state.showSensorChart = false;
};
const onSensorChartClick = (dt: Date) => {
  if (!dt) return;

  dt = new Date(dt);
  const dateDisp = dtFormat(dt, 'yyyy/mm/dd');
  const timeDisp = dtFormat(dt, 'HH:MM:SS');
  emit('move-to-time', { dt, dateDisp, timeDisp });
};
const copyLink = () => {
  const dt = ensureDate(`${dateDisp.value} ${state.timeDisp}`);
  if (!props.movie?.ts || !dt) return;

  const offset = dt.getTime() - props.movie.ts.getTime();
  const link = `${location.origin}/?movie_id=${props.movie.id}&start_offset=${offset}`;
  window.navigator.clipboard.writeText(link);
};

defineExpose({ setAllUIStateToDefault, setSensorData });
</script>

<style lang="scss" scoped>
.row1, .row3 {
  display: flex;
  flex-wrap: nowrap;
}
.row1 {
  align-items: center;
  .spacer {
    flex-grow: 1;
  }
}
.row3 {
  .edit-tag-btn {
    align-self: flex-start;
  }
  .tags {
    display: flex;
    flex-wrap: wrap;
    .tag-badge {
      align-self: center;
    }
  }
}
</style>
